import { useEffect, useState } from 'react'

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

// jsdom does not implement window.matchMedia, and several in-repo suites mock it
// per-file for that reason (e.g. Navbar/Left/index.test.tsx:22). Guarding HERE
// rather than at the call site keeps the suites that mount the real
// WizardSimulator from throwing on a helper they never asked for; the un-mocked
// default is "motion allowed", which is what those suites already assume.
const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches

/**
 * Whether the OS asks for reduced motion. Subscribes to changes so a mid-session
 * toggle takes effect without a reload.
 */
export const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(prefersReducedMotion)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const query = window.matchMedia(REDUCED_MOTION_QUERY)
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches)
    // Safari < 14 shipped only the deprecated addListener, and this repo's
    // browserslist (">0.2%, not dead") still includes it.
    if (query.addEventListener) {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }
    query.addListener(onChange)
    return () => query.removeListener(onChange)
  }, [])

  return reduced
}

export default usePrefersReducedMotion
